import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Radio,
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import Loader from "@icarius-common/loader";
import { roles } from "@icarius-utils/constants";
import { getModulesTreeAction } from "@icarius-pages/modulesAndMenus/actions";
import { Alert } from "@material-ui/lab";
import ModulesAndMenusTree from "../../modules/treeDialog/modulesAndMenusTree";
import useHandleForm from "./useHandleForm";

const DuplicateExclusionsFormDialog = (props) => {

  const {
    open,
    data,
    moduleList,
    tree,
    submitCallback,
    handleClose,
    isLoading,
  } = props;

  const dispatch = useDispatch();

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    formData,
    setFormValue,
    submit,
  } = useHandleForm(data, submitCallback, openValidationError);

  const [role, setRole] = useState(data?.role || "E");
  const [isTypeModule, setIsTypeModule] = useState(Boolean(data?.module));

  useEffect(() => {
    dispatch(getModulesTreeAction(role, ""));
  }, [dispatch, role])

  const handleSelectMenu = (newMenuId) => {
    if (!newMenuId) {
      setFormValue([], "menu");
      return;
    }

    let newMenus = formData.menu;
    if (newMenus.includes(newMenuId)) {
      newMenus = newMenus.filter((oldMenuId) => oldMenuId !== newMenuId);
    } else {
      newMenus = [...newMenus, newMenuId];
    }

    setFormValue(newMenus, "menu")
  }

  const menuName = useMemo(() => { // FIX
    if (!formData.menu.length) return "";
    if (!tree?.modules?.length) return "";

    let auxNames = [];
    const searchInBranch = (branch) => {
      branch.forEach((item) => {
        if (formData.menu.includes(item.id)) auxNames.push(item.name);
        if (item.menus) searchInBranch(item.menus);
      })
    }

    searchInBranch(tree.modules);
    return auxNames.join(", ");
  }, [tree.modules, formData.menu])

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"sm"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
          {getLocalizedString("duplicateExclusion")}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent style={{ paddingTop: 0 }}>
          <Loader open={isLoading} />
          <Grid container item xs={12} spacing={2}>
            <Grid container item alignItems="center" xs={12}>
              <Grid container item xs={12}>
                <Typography className="whiteText" variant="subtitle1" style={{ display: "inline-block", marginRight: 20 }}>
                  {getLocalizedString("applyExclusionTo")}
                </Typography>
              </Grid>
              <Grid container item xs={12}>
                <div
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", marginRight: 20 }}
                  onClick={() => {
                    setIsTypeModule(true);
                    handleSelectMenu(null);
                  }}
                >
                  <Radio color="primary" checked={isTypeModule} />
                  <Typography className="whiteText">
                    {getLocalizedString("module")}
                  </Typography>
                </div>
                <div
                  style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                  onClick={() => {
                    setIsTypeModule(false);
                    setFormValue([], "module");
                  }}
                >
                  <Radio color="primary" checked={!isTypeModule} />
                  <Typography className="whiteText">
                    {getLocalizedString("menu")}
                  </Typography>
                </div>
              </Grid>
            </Grid>
            {
              isTypeModule &&
              <Grid container item alignItems="center" xs={12}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel required id={`label-module`}>{getLocalizedString("module")}</InputLabel>
                  <Select
                    value={formData.module}
                    labelId={`label-module`}
                    id={`select-module`}
                    onChange={(e) => setFormValue(e.target.value, "module")}
                    margin={"none"}
                    multiple
                  >
                    {
                      moduleList.map((item) => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}
                        >
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            }
            {
              !isTypeModule &&
              <>
                <Grid container item xs={12}>
                  <FormControl style={{ width: "100%" }}>
                    <InputLabel id={'label-role'}>{getLocalizedString("role")}</InputLabel>
                    <Select
                      value={role}
                      labelId={'label-role'}
                      id={'select-role'}
                      onChange={(e) => {
                        setRole(e.target.value);
                        handleSelectMenu(null);
                      }}
                      margin={"none"}
                    >
                      {
                        Object.keys(roles).map((roleName) => ({ key: roles[roleName], value: getLocalizedString(roleName) }))
                          .map(item => (
                            <MenuItem
                              className={"whiteText"}
                              key={item.key}
                              value={item.key}
                            >
                              {item.value}
                            </MenuItem>
                          ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid container item xs={12} direction="column">
                  <Typography className="whiteText" variant="caption">
                    {getLocalizedString("menu")}
                    <span style={{ fontWeight: 400, fontSize: 16, marginLeft: 5 }} className="errorColor">
                      {"*"}
                    </span>
                  </Typography>
                  <Typography className="whiteText">
                    {menuName || getLocalizedString("none")}
                  </Typography>
                  {
                    Boolean(tree?.modules?.length) &&
                    <div style={{ marginTop: 15, display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                      <Alert variant="filled" severity="info">
                        {getLocalizedString("clickToSelectMenu")}
                      </Alert>
                      <ModulesAndMenusTree
                        data={tree}
                        selectedIds={formData.menu}
                        handleClick={(newMenu) => handleSelectMenu(newMenu.id)}
                      />
                    </div>
                  }
                </Grid>
              </>
            }
          </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
          <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default DuplicateExclusionsFormDialog;
